// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module SessionReplay
 * @package @geenius/analytics
 * @description Shared helpers for materializing lightweight session-replay
 * snapshots from events and page views. The Convex layer and UI variants use
 * these helpers to produce a consistent replay model.
 */

import type {
  AnalyticsEvent,
  PageView,
  SessionReplayFrame,
  SessionReplaySnapshot,
} from "./types";

function normalizeEvents(
  events: Array<AnalyticsEvent | PageView>,
): SessionReplayFrame[] {
  return events
    .map((entry, index) => {
      const isPageView = "title" in entry;
      const kind = isPageView ? "page_view" : "event";
      const label = isPageView ? entry.title : entry.name;
      return {
        id: `${kind}_${entry.sessionId}_${entry.timestamp}_${index}`,
        kind,
        label,
        url: entry.url,
        timestamp: entry.timestamp,
        deltaMs: 0,
        sessionId: entry.sessionId,
        userId: entry.userId,
        properties: isPageView
          ? entry.duration !== undefined
            ? { duration: entry.duration }
            : undefined
          : entry.properties,
      } satisfies SessionReplayFrame;
    })
    .sort((left, right) => left.timestamp - right.timestamp)
    .map((frame, index, list) => ({
      ...frame,
      deltaMs:
        index === 0
          ? 0
          : frame.timestamp - (list[index - 1]?.timestamp ?? frame.timestamp),
    }));
}

/**
 * Builds a lightweight session-replay snapshot from events and page views.
 *
 * @param events Analytics events and page views to normalize into replay frames.
 * @param sessionId Session identifier whose ordered activity should be materialized.
 * @returns Session replay snapshot with ordered frames and derived duration metadata.
 */
export function buildSessionReplay(
  events: Array<AnalyticsEvent | PageView>,
  sessionId: string,
): SessionReplaySnapshot {
  const sessionEvents = events.filter((event) => event.sessionId === sessionId);
  const frames = normalizeEvents(sessionEvents);
  const timestamps = frames.map((frame) => frame.timestamp);

  const startedAt = timestamps[0] ?? Date.now();
  const endedAt = timestamps[timestamps.length - 1] ?? startedAt;

  return {
    sessionId,
    startedAt,
    endedAt,
    duration: timestamps.length > 1 ? endedAt - startedAt : 0,
    frames,
    activePages: [...new Set(frames.map((frame) => frame.url).filter(Boolean))],
    userId: frames.find((frame) => frame.userId)?.userId,
  };
}

/**
 * Returns the ordered frame list from a session-replay snapshot.
 *
 * @param snapshot Session replay snapshot to inspect.
 * @returns Replay frames in playback order.
 */
export function replaySessionFrames(
  snapshot: SessionReplaySnapshot,
): SessionReplayFrame[] {
  return snapshot.frames;
}
